/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {CSSProperties} from "react";
import {BaseComponent, TS_Checkbox} from "@intuitionrobotics/thunderstorm/frontend";
import {tableCardStyle} from "../../home/AlertsTab";
import {TS_Table} from "../../../_TS_TABLE";
import {COLORS} from "@res/colors";
import {DB_User, getUserNameWithTitle, UserRole} from "@app/ir-q-app-common/types/care-center/user";
import {OnUsersUpdated, UsersModule} from "@modules/UsersModule";
import {padding_rl_med} from "@styles/styles";
import {FONTS} from "@res/fonts";
import * as emotion from "emotion";
import {Loader} from "../../../../widgets/Loader";
import {addItemToArray, generateHex, removeItemFromArray} from "@intuitionrobotics/ts-common";
import {FieldEditorClick} from "@intuitionrobotics/thunderstorm/app-frontend/widgets/FieldEditorClick";
import {ICONS} from "@res/icons";
import {SearchBar} from "../SearchBar";
import {ChangePasswordDialog} from "../users/ChangePasswordDialog";
import {hideTooltip, showTooltip} from "../../widget/appTooltip";
import {PermissionDialog} from "../permissions/PermissionDialog";
import {PermissionsContext} from "./AdminTab";
import {OKButton} from "../../widget/OKButton";
import {AddUserDialog} from "../users/AddUserDialog";
import {AppPermissions} from "../../../utils/Permissions";
import {AppRoleLevel} from "@app/app-shared/permission";

type UserTableRow = {
    displayName: DB_User
    first: DB_User
    last: DB_User
    roles: DB_User,
    permissions: DB_User,
    title: DB_User,
    mailEnabled: DB_User,
    email: string,
    actions: DB_User
}

type State = {
    loading: boolean,
    searchText: string
}

export class AdminUserTab
    extends BaseComponent<{}, State>
    implements OnUsersUpdated {

    __onUsersUpdated() {
        this.setState({loading: false});
    }

    constructor(props: {}) {
        super(props);
        this.state = {
            loading: false,
            searchText: ''
        };
    }

    private getUserContent = (users: DB_User[]): UserTableRow[] => (
        users.map(_user => ({
            displayName: _user,
            first: _user,
            last: _user,
            roles: _user,
            permissions: _user,
            title: _user,
            mailEnabled: _user,
            email: _user.mail,
            actions: _user
        })).filter(({first: user}) => {
            return !![getUserNameWithTitle(user), user.first, user.last, user.mail]
                .find(value => value?.toLowerCase().includes(this.state.searchText.toLowerCase()));
        }));

    headerStyle = (title?: string, style?: CSSProperties) => {
        return <div className={`ll_h_c ${padding_rl_med}`}
                    style={{...style, height: 34, backgroundColor: COLORS.color_gray_medium}}>
            {title && FONTS.reg_14_secondary_dark(title, {whiteSpace: "nowrap"})}
        </div>;
    };

    rowStyle = (content: string) => {
        const textStyle = {color: COLORS.neutral_text_dark, lineHeight: "24px"}; // line height should be at least half of row height for maximum of 2 lines
        const blockWithText = emotion.css({maxHeight: "47px"});

        return <div
            className={`ll_h_c ${padding_rl_med}`}
            style={{height: 47, cursor: "pointer"}}>
            <div className={blockWithText}>
                {FONTS.reg_16(content, textStyle)}
            </div>
        </div>;
    };

    private headers: (keyof UserTableRow)[] = ["displayName", "first", "last", "roles", "permissions", "title", "mailEnabled", "email", "actions"];

    renderUserTable = () => {
        const users = UsersModule.getUsers();
        return <div style={{...tableCardStyle, minHeight: "80%", minWidth: "80%"}}>
            <PermissionsContext.Consumer>
                {
                    ({permissions, upsertPermission}) => <TS_Table<UserTableRow>
                        id={"admin-table"}
                        header={this.headers}
                        rows={this.getUserContent(users)}
                        td={{
                            style: {borderBottom: `1px solid ${COLORS.grays_2}`}
                        }}
                        style={{width: "100%"}}
                        headerRenderer={{
                            displayName: () => this.headerStyle("Display Name"),
                            first: () => this.headerStyle("First Name"),
                            last: () => this.headerStyle("Last Name"),
                            roles: () => this.headerStyle("Roles"),
                            permissions: () => this.headerStyle("Permissions"),
                            title: () => this.headerStyle("Title"),
                            mailEnabled: () => this.headerStyle("Mail Enabled"),
                            email: () => this.headerStyle("Email"),
                            actions: () => this.headerStyle("Actions", {justifyContent: "center"})
                        }}
                        cellRenderer={{
                            email: (email: string) => {
                                return <div className={`ll_h_c ${padding_rl_med}`}
                                            style={{justifyContent: "space-evenly"}}>
                                    {email}
                                </div>;
                            },
                            mailEnabled: (user: DB_User) => {
                                return <div className={`ll_h_c ${padding_rl_med}`}
                                            style={{justifyContent: "space-evenly"}}>
                                    <TS_Checkbox
                                        value={user.mailEnabled}
                                        checked={!!user.mailEnabled}
                                        label={"Mail Enabled"}
                                        onCheck={() => {
                                            const toUpdate = {
                                                mailEnabled: !user.mailEnabled,
                                                _id: user._id
                                            } as DB_User;
                                            this.setState({loading: true}, () => UsersModule.update(toUpdate));
                                        }}/>
                                </div>;
                            },
                            permissions: (user: DB_User) => {
                                const dbPermission = permissions.find(permission => user.permissionId === permission.id);
                                return <div
                                    key={"edit"}
                                    className={`ll_h_c ${dbPermission ? "clickable" : ""}`}
                                    onClick={() => dbPermission && PermissionDialog.show(permissions, upsertPermission, dbPermission, user._id)}
                                    onMouseEnter={(e) => dbPermission && showTooltip(e, "View Permissions")}
                                    onMouseLeave={() => hideTooltip()}
                                >
                                    {dbPermission ? dbPermission.name : "None"}
                                </div>;
                            },
                            actions: (user: DB_User) => {
                                const isIr = user.mail.endsWith("@intuitionrobotics.com");
                                return !isIr && <div
                                    key={"edit"}
                                    className={"ll_h_c"}
                                    onClick={() => ChangePasswordDialog.show(user)}
                                    onMouseEnter={(e) => showTooltip(e, "Change Password")}
                                    onMouseLeave={() => hideTooltip()}
                                    style={{cursor: "pointer", justifyContent: "space-around"}}
                                >
                                    {ICONS.icon_filters(COLORS.grays_dark, 20)}
                                </div>;
                            },
                            displayName: (cellValue: DB_User) => <div>
                                <div style={{
                                    alignSelf: "flex-start",
                                    height: "35px"
                                }} className="ll_h_c">
                                    {this.rowStyle(getUserNameWithTitle(cellValue))}</div>
                            </div>,
                            first: (user: DB_User) => {
                                return <FieldEditorClick
                                    id={generateHex((8))}
                                    value={user.first}
                                    labelStyle={{background: "#dedede", width: 100, height: 20}}
                                    inputStyle={{maxWidth: 80}}
                                    placeholder={"Double click to add"}
                                    onAccept={(first?: string) => {
                                        if (!first)
                                            return;

                                        user.first = first;
                                        this.setState({loading: true}, () => UsersModule.update(user));
                                    }}
                                />;
                            },
                            last: (user: DB_User) => {
                                return <FieldEditorClick
                                    id={generateHex((8))}
                                    value={user.last}
                                    labelStyle={{background: "#dedede", width: 100, height: 20}}
                                    inputStyle={{maxWidth: 80}}
                                    placeholder={"Double click to add"}
                                    onAccept={(last?: string) => {
                                        if (!last)
                                            return;

                                        user.last = last;
                                        this.setState({loading: true}, () => UsersModule.update(user));
                                    }}
                                />;
                            },
                            roles: (user: DB_User) => {
                                const allRoles: UserRole[] = [UserRole.Physician, UserRole.CareManager];
                                return <div className={`${padding_rl_med}`}
                                            style={{display: "flex", justifyContent: "space-evenly"}}>
                                    {allRoles.map(role => {
                                        return <TS_Checkbox
                                            key={role}
                                            value={role}
                                            checked={!!user.roles?.find(r => r === role)}
                                            label={role}
                                            onCheck={() => {
                                                user.roles = user.roles || [];
                                                if (user.roles.includes(role)) {
                                                    removeItemFromArray(user.roles, role);
                                                } else {
                                                    addItemToArray(user.roles, role);
                                                }
                                                this.setState({loading: true}, () => UsersModule.update(user));
                                            }}/>;
                                    })}
                                </div>;
                            },
                            title: (user: DB_User) => {
                                return <FieldEditorClick
                                    id={generateHex((8))}
                                    value={user.title}
                                    labelStyle={{background: "#dedede", width: 100, height: 20}}
                                    inputStyle={{maxWidth: 100}}
                                    placeholder={"Double click to add"}
                                    onAccept={(title?: string) => {
                                        if (!title)
                                            return;

                                        user.title = title;
                                        this.setState({loading: true}, () => UsersModule.update(user));
                                    }}
                                />;
                            }
                        }}
                    />

                }
            </PermissionsContext.Consumer>
        </div>;
    };

    render() {
        return <AppPermissions appRoles={[AppRoleLevel.SuperAdmin]}>
            <div
                style={{overflow: "scroll", marginTop: "15px"}}
                className={"match_height"}
            >
                <div className={"ll_h_c"}>
                    <SearchBar
                        value={this.state.searchText}
                        onValueChange={(value) => this.setState({searchText: value})}
                        onClose={() => this.setState({searchText: ''})}
                        placeholder={"Search by user"}
                    />
                    <OKButton
                        text={"Add User"}
                        enabled={true}
                        onClick={() => AddUserDialog.show()}
                        wrapperStyle={{
                            minHeight: "unset",
                            minWidth: "unset"
                        }}
                        inverted
                    />
                </div>
                <div style={{...tableCardStyle, minHeight: "80%", minWidth: "80%"}}>
                    {this.renderUserTable()}
                    {this.state.loading && <Loader overlay/>}
                </div>
            </div>
        </AppPermissions>
    }
}
